// src/hooks/useTourLegs.ts
//
// Resolve walking-distance + duration for each leg of a tour, plus
// the total across all legs. Backed by the `walkingTourRoute`
// Cloud Function which makes one Directions API call regardless
// of waypoint count.
//
// Re-fetches when the waypoint coordinates change. The cache key
// is the joined "lat,lng;lat,lng;..." string — stable across
// re-renders so React.useEffect doesn't refetch on every parent
// render. Identity-changing the waypoints array (same coords,
// different reference) is fine because we hash the coords.
//
// Returns null while loading and on failure. Consumers should
// guard renders on the data being non-null and treat absence as
// "feature unavailable" rather than "zero distance."

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';

export interface TourLeg {
  /** Walking distance from waypoint[i] → waypoint[i+1] in meters. */
  distance_m: number | null;
  /** Walking duration from waypoint[i] → waypoint[i+1] in seconds. */
  duration_s: number | null;
}

export interface TourLegsResult {
  /** legs[i] is the path FROM waypoint[i] TO waypoint[i+1].
   *  legs.length === waypoints.length - 1 in the normal case. */
  legs: TourLeg[];
  /** Sum of all legs' distance_m. */
  total_distance_m: number;
  /** Sum of all legs' duration_s. */
  total_duration_s: number;
}

interface InputWaypoint {
  lat: number;
  lng: number;
}

export function useTourLegs(
  waypoints: InputWaypoint[]
): TourLegsResult | null {
  const [data, setData] = useState<TourLegsResult | null>(null);

  // Stable cache key — identity of the waypoints array doesn't
  // matter, only the coords. This avoids refetching when the
  // parent re-renders with a fresh array containing the same
  // values (very common in React).
  const key = waypoints.map((w) => `${w.lat},${w.lng}`).join(';');

  useEffect(() => {
    if (waypoints.length < 2) {
      setData(null);
      return;
    }

    let cancelled = false;
    const callable = httpsCallable(functions, 'walkingTourRoute');

    callable({
      waypoints: waypoints.map((w) => ({ lat: w.lat, lng: w.lng })),
    })
      .then((res) => {
        if (cancelled) return;
        const result = (res.data as { result?: TourLegsResult | null })?.result;
        // result === null means the backend gave up (validation,
        // API failure, etc.) — keep state null so the UI hides
        // the metrics. Don't set zero/empty data; that would lie.
        if (result !== null && result !== undefined) {
          setData(result);
        }
      })
      .catch((err) => {
        // Network failure or callable threw — same treatment as
        // null result. Quietly degrade.
        console.warn('useTourLegs fetch failed:', err);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return data;
}

/** Format meters as "120 m" / "1.2 km" / "12 km". Tunable rounding
 *  per range. Used by both the per-leg display and the total. */
export const formatLegDistance = (m: number | null): string => {
  if (m === null) return '';
  if (m < 100) return `${m} m`;
  if (m < 1000) return `${Math.round(m / 10) * 10} m`;
  return `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km`;
};

/** Format seconds as "8 min" / "1 hr 5 min" / "<1 min". */
export const formatLegDuration = (s: number | null): string => {
  if (s === null) return '';
  if (s < 60) return '<1 min';
  const min = Math.round(s / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  const rem = min % 60;
  return rem === 0 ? `${hr} hr` : `${hr} hr ${rem} min`;
};
